import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';

import { api } from '../lib/api';

/**
 * Site-wide announcement banner — Saket recommendation 4.
 *
 * Reads copy from `menu_settings.announcement_*` (admin-editable in
 * Admin → Menu → Settings). Sticky at the very top of the viewport,
 * above the site nav. Dismissible per user via localStorage; the
 * `announcement_dismiss_key` field lets the operator force a re-show
 * for a *new* announcement even if the diner dismissed the previous
 * one (change the key when copy changes).
 *
 * Kind → colour palette:
 *   • 'launch'    — teal/gold (new outlet opening, hot)
 *   • 'event'     — aubergine/gold (walks, gate events)
 *   • 'menu-drop' — cream/aubergine (vote / suggest live)
 *   • 'info'      — muted (default)
 */
const PALETTE = {
  launch:      { bg: 'linear-gradient(90deg,#0A6A5A 0%,#0F8A73 55%,#E8B84B 100%)', ink: '#F5EFF7', kicker: '#E8B84B' },
  event:       { bg: 'linear-gradient(90deg,#3A1140 0%,#531B55 60%,#7C2B29 100%)', ink: '#F5EFF7', kicker: '#E8B84B' },
  'menu-drop': { bg: 'linear-gradient(90deg,#5C1E5C 0%,#8A2A6E 100%)',              ink: '#F5EFF7', kicker: '#E8B84B' },
  info:        { bg: '#26202C',                                                     ink: '#F5EFF7', kicker: '#E8B84B' },
};

const STORAGE_PREFIX = 'xin-announce-dismissed:';

export default function AnnouncementBanner() {
  const [banner, setBanner] = useState(null);   // { text, kind, cta_label, cta_href, dismiss_key }
  const [dismissed, setDismissed] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    let cancelled = false;
    api.menu().then((data) => {
      if (cancelled) return;
      const s = data?.settings || {};
      if (!s.announcement_enabled || !s.announcement_text) return;
      const b = {
        kind: s.announcement_kind || 'info',
        text: s.announcement_text,
        cta_label: s.announcement_cta_label || '',
        cta_href: s.announcement_cta_href || '',
        dismiss_key: s.announcement_dismiss_key || 'v1',
      };
      // Respect prior dismissal *only if the dismiss_key matches* — a
      // fresh dismiss_key means new announcement, force re-show.
      try {
        const stored = window.localStorage.getItem(STORAGE_PREFIX + b.dismiss_key);
        if (stored === '1') { setDismissed(true); }
      } catch { /* private mode */ }
      setBanner(b);
    }).catch(() => { /* no banner if menu API is down */ });
    return () => { cancelled = true; };
  }, []);

  if (!banner || dismissed) return null;
  if (pathname.startsWith('/mira') || pathname.startsWith('/table') || pathname.startsWith('/wall')) return null;

  const p = PALETTE[banner.kind] || PALETTE.info;
  const hasCta = !!(banner.cta_label && banner.cta_href);
  const isExternal = /^https?:\/\//.test(banner.cta_href);

  const dismiss = () => {
    setDismissed(true);
    try { window.localStorage.setItem(STORAGE_PREFIX + banner.dismiss_key, '1'); } catch { /* silent */ }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="site-announcement-banner"
      data-kind={banner.kind}
      style={{
        position: 'sticky', top: 0, left: 0, right: 0, zIndex: 60,
        background: p.bg,
        color: p.ink,
        borderBottom: `1px solid rgba(232,184,75,0.4)`,
      }}
    >
      <div className="mx-auto max-w-[1400px] flex items-center gap-3 px-4 sm:px-6"
           style={{ padding: '9px 14px' }}>
        <span
          aria-hidden
          className="hidden sm:inline-block flex-shrink-0"
          style={{ width: 7, height: 7, borderRadius: '50%', background: p.kicker, boxShadow: `0 0 10px ${p.kicker}` }}
        />
        <div className="flex-1 min-w-0 flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-[10px] font-bold uppercase tracking-[2.5px] flex-shrink-0"
                style={{ color: p.kicker }}>
            {banner.kind === 'launch' ? 'Now live' :
             banner.kind === 'event' ? 'This week' :
             banner.kind === 'menu-drop' ? 'Menu drop' : 'Announcement'}
          </span>
          <span className="text-[12.5px] sm:text-[13.5px]" style={{ color: p.ink }}>
            {banner.text}
          </span>
          {hasCta && (
            isExternal ? (
              <a href={banner.cta_href}
                 target="_blank" rel="noreferrer"
                 className="text-[12px] font-bold underline underline-offset-2"
                 style={{ color: p.kicker }}
                 data-testid="site-announcement-cta">
                {banner.cta_label} ↗
              </a>
            ) : (
              <a href={banner.cta_href}
                 className="text-[12px] font-bold underline underline-offset-2"
                 style={{ color: p.kicker }}
                 data-testid="site-announcement-cta">
                {banner.cta_label} →
              </a>
            )
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss announcement"
          className="flex-shrink-0 p-1.5 rounded-full transition-opacity opacity-70 hover:opacity-100"
          style={{ background: 'rgba(0,0,0,0.15)', color: p.ink }}
          data-testid="site-announcement-dismiss"
        >
          <X size={14} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}
